import { useEffect, useState } from "react";
import { useLang } from "../context/LanguageContext";
import { ASSETS } from "../i18n";

const NAV_DESKTOP = ["origin", "operations", "standards", "trade"];
const NAV_MOBILE = ["origin", "operations", "standards", "trade", "inquiries"];

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const light = !scrolled; // transparent header over the dark hero

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="header-logo"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center gap-3 group"
        >
          <img src={light ? ASSETS.sealLight : ASSETS.sealDark} alt="TOTUMO" className="h-9 w-auto transition-opacity duration-500" />
          <span className={`font-serif text-xl tracking-[0.35em] hidden sm:block transition-colors duration-500 ${light ? "text-ivory" : "text-ink"}`}>TOTUMO</span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_DESKTOP.map((s) => (
            <button
              key={s}
              data-testid={`nav-${s}`}
              onClick={() => go(s)}
              className={`font-sans text-[0.7rem] uppercase tracking-[0.18em] relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-500 hover:after:w-full transition-colors duration-500 ${light ? "text-ivory/80 hover:text-ivory" : "text-ink/70 hover:text-ink"}`}
            >
              {t(`nav.${s}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            aria-label="Toggle language"
            className={`font-sans text-[0.7rem] tracking-[0.2em] transition-colors duration-500 ${light ? "text-ivory/70" : "text-ink/70"}`}
          >
            <span className={lang === "en" ? (light ? "text-ivory" : "text-ink") : (light ? "text-ivory/40" : "text-ink/40")}>EN</span>
            <span className={light ? "mx-1 text-ivory/30" : "mx-1 text-ink/30"}>/</span>
            <span className={lang === "es" ? (light ? "text-ivory" : "text-ink") : (light ? "text-ivory/40" : "text-ink/40")}>ES</span>
          </button>

          <button
            data-testid="header-inquiries-cta"
            onClick={() => go("inquiries")}
            className={`hidden md:inline-block font-sans text-[0.7rem] uppercase tracking-[0.18em] px-5 py-2.5 transition-all duration-500 ${light ? "border border-ivory/60 text-ivory hover:bg-ivory hover:text-ink" : "border border-ink/60 text-ink hover:bg-ink hover:text-ivory"}`}
          >
            {t("cta.inquiries")}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden flex flex-col gap-1.5 w-7"
            aria-label="Menu"
          >
            <span className={`h-px transition-all duration-300 ${light ? "bg-ivory" : "bg-ink"} ${open ? "rotate-45 translate-y-2" : ""}`} />
            <span className={`h-px transition-all duration-300 ${light ? "bg-ivory" : "bg-ink"} ${open ? "opacity-0" : ""}`} />
            <span className={`h-px transition-all duration-300 ${light ? "bg-ivory" : "bg-ink"} ${open ? "-rotate-45 -translate-y-2" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-ink/10 px-6 py-8 flex flex-col gap-5">
          {NAV_MOBILE.map((s) => (
            <button
              key={s}
              data-testid={`mobile-nav-${s}`}
              onClick={() => go(s)}
              className="text-left font-sans text-sm uppercase tracking-[0.18em] text-ink/80"
            >
              {t(`nav.${s}`)}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};
